<template>

<v-row style="margin:0em;">
    <v-col cols="12" md="7" lg="7">
    <panel fill-height>
        <template v-slot:header>
            Entry: {{ response ? response.accession : "Loading..." }}
        </template>

        <template v-slot:toolbar-extra>
            <v-chip v-if="response && response.warning == true" color="error">Warning</v-chip>

            <!--
                MSA/PAE download and MSA conservation are disabled for the
                time being: none of them are provided yet.
            -->
            <v-menu v-if="false" offset-y left>
                <template v-slot:activator="{ props }">
                    <v-btn variant="plain" v-bind="props">
                        <v-icon>{{ $MDI.NotificationClearAll }}</v-icon>
                        MSA
                    </v-btn>
                </template>
                <v-list>
                    <v-list-item>
                        <template v-slot:prepend>
                            <v-icon>{{ $MDI.FileDownloadOutline }}</v-icon>
                        </template>
                        <v-list-item-title>
                            MSA (.a3m)
                        </v-list-item-title>
                    </v-list-item>
                    <v-list-item :href="'https://bfvd.steineggerlab.workers.dev/pae/' + response.accession + '.json'">
                        <template v-slot:prepend>
                            <v-icon>{{ $MDI.FileDownloadOutline }}</v-icon>
                        </template>
                        <v-list-item-title>
                            PAE (.json)
                        </v-list-item-title>
                    </v-list-item>
                    <v-dialog v-model="dialog" fullscreen>
                        <template v-slot:activator="{ props }">
                            <v-list-item v-bind="props">
                                <template v-slot:prepend>
                                    <v-icon>{{ $MDI.ChartBarStacked }}</v-icon>
                                </template>
                                <v-list-item-title>
                                    Conservation
                                </v-list-item-title>
                            </v-list-item>
                        </template>

                        <Panel>
                            <template v-slot:header>
                                MSA conservation
                            </template>

                            <template v-slot:toolbar-extra>
                                <v-btn color="primary" variant="text" @click="dialog = false">
                                    Close
                                </v-btn>
                            </template>

                            <template v-slot:content>
                                <MsaLogoPlot :accession="$route.params.cluster"></MsaLogoPlot>
                            </template>
                        </Panel>
                    </v-dialog>
                </v-list>
            </v-menu>
        </template>

        <template v-slot:content>
            <template v-if="response">
            <h3>Entry summary</h3>
            <dl class="dl-4">
                <div>
                <dt>
                    Accession
                </dt>
                <dd>
                    <ExternalLinks :accession="response.accession"></ExternalLinks>
                </dd>
                </div>
                <div>
                <dt>
                    Length
                </dt>
                <dd>
                    {{ response.len }} aa
                </dd>
                </div>
                <div>
                <dt>
                    pLDDT
                </dt>
                <dd>
                    {{ formatNumber(response.plddt) }}
                </dd>
                </div>
                <div>
                <dt>
                    Singleton cluster
                </dt>
                <dd>
                    {{ response.is_singleton ? 'yes' : 'no' }}
                </dd>
                </div>

                <div>
                <dt>
                    Predictor
                </dt>
                <dd>
                    <Fragment :flag="response.flag"></Fragment>
                </dd>
                </div>
                <div>
                <dt>
                    Proteome
                </dt>
                <dd>
                    <template v-if="response.proteome">
                        <a :href="'https://www.uniprot.org/proteomes/' + response.proteome" target="_blank" rel="noopener">{{ response.proteome }}</a>
                    </template>
                    <template v-else>NA</template>
                </dd>
                </div>
                <div style="grid-area: 2 / 3 / 3 / 5;">
                <dt>
                    Host
                </dt>
                <dd>
                    <!-- UniProt names an organism, ICTV only a coarse category. -->
                    <template v-if="response.host_source === 'uniprot'">
                        <template v-for="(taxonomy, index) in response.hosts" :key="taxonomy.id"><TaxSpan :taxonomy="taxonomy"></TaxSpan><template v-if="index < (response.hosts.length -1)">,&nbsp;</template></template>
                    </template>
                    <template v-else-if="response.host_source === 'ictv'">
                        {{ response.ictv.host_category }}
                    </template>
                    <template v-else>NA</template>
                </dd>
                </div>

                <!-- The protein name and taxonomy run long, so each spans the grid
                     rather than being squeezed into a quarter-width column. -->
                <div style="grid-area: 3 / 1 / 4 / 5;">
                <dt>
                    Protein name
                </dt>
                <dd>
                    {{ response.description ? response.description.trim() : 'NA' }}
                </dd>
                </div>

                <div style="grid-area: 4 / 1 / 5 / 5;">
                <dt>
                    Taxonomy<a v-if="response.ictv && response.ictv.id" class="annot-label" :href="'https://ictv.global/id/' + response.ictv.id" target="_blank" rel="noopener">{{ response.ictv.id }}</a>
                </dt>
                <dd>
                    <template v-for="(taxonomy, index) in response.lineage_entry" :key="taxonomy.id"><TaxSpan :taxonomy="taxonomy"></TaxSpan><template v-if="index < (response.lineage_entry.length -1)"> &#187;&nbsp;</template></template>
                </dd>
                </div>
                </dl>
                <v-divider  style="margin-top:0.5em"></v-divider>
                <h3 style="margin-top:1em">
                    Sequence cluster summary
                    <!--<v-tooltip top>
                        <template v-slot:activator="{ on }">
                            <span v-on="on">
                                <v-icon v-on="on">{{ $MDI.HelpCircleOutline }}</v-icon>
                            </span>
                        </template>
                        <span>
                            These values are computed among the members with the <strong>clustered step</strong> AFDB/Foldseek.
                        </span>
                    </v-tooltip>-->
                </h3>
                <dl class="dl-3">
                <div>
                <dt>
                    Number of members
                </dt>
                <dd>
                    {{ response.n_mem }}
                </dd>
                </div>
                <div>
                <dt>
                    Average length
                </dt>
                <dd>
                    {{ formatNumber(response.avg_len) }} aa
                </dd>
                </div>
                <!-- <div>
                <dt>
                    Average pLDDT
                </dt>
                <dd>
                    {{ response.avg_plddt.toFixed(2) }}
                </dd>
                </div> -->
                <div style=" grid-area: 2 / 1 / 3 / 4;">
                <dt>
                    Lowest common ancestor and lineage
                </dt>
                <dd>
                    <template v-for="(taxonomy, index) in response.lineage" :key="taxonomy.id"><TaxSpan :taxonomy="taxonomy"></TaxSpan><template v-if="index < (response.lineage.length -1)"> &#187;&nbsp;</template></template>
                </dd>
                </div>
                <!-- <div style=" grid-area: 3 / 1 / 3 / 4;">
                    <dt>Annotations</dt>
                    <dd>
                        <Annotations :cluster="$route.params.cluster"></Annotations>
                    </dd>
                </div> -->
            </dl>
            <template v-if="response && response.warning == true">
                <v-divider  style="margin-top:0.5em"></v-divider>
                <h3 style="margin-top:1em; color: #F44336; text-decoration: underline;">
                    Warning!
                </h3>
                <p>
                    This cluster was wrongly merged with another cluster. We are working on a fix.
                </p>
            </template>
            </template>
        </template>
    </panel>
    </v-col>
    <v-col cols="12" md="5" lg="5">
    <Panel class="repr-structure" fill-height >
        <template v-slot:header>
            Structure
        </template>
        
        <template v-slot:toolbar-extra>
            <v-btn v-if="response" variant="plain" :href="'https://bfvd.steineggerlab.workers.dev/pdb/' + response.accession + '.pdb'">
                <v-icon class="mr-1">{{ $MDI.FileDownloadOutline }}</v-icon>
                PDB
            </v-btn>
        </template>
        
        <template v-slot:content>
            <StructureViewer v-if="response && $route.params.cluster" :cluster="$route.params.cluster" :second="second" bgColorDark="#2e2e2e" @reset="second = ''"></StructureViewer>
        </template>
    </Panel>
    </v-col>

    <v-col cols="12">
        <Members :cluster="$route.params.cluster" @select="(accession) => second = accession"></Members>
    </v-col>

    <v-col cols="12">
        <Similars :cluster="$route.params.cluster" @select="(accession) => second = accession"></Similars>
    </v-col>

    <v-col cols="12" class="mt-3">
        <v-card rounded="0">
            <v-col>
            <v-card-title primary-title class="pb-0 mb-0">
                <div class="text-h5 mb-0">Reference</div>
            </v-card-title>
            <v-card-title primary-title class="pt-0 mt-0">
                <p class="text-subtitle-1 mb-0" style="word-break: break-word;">
                    Kim&nbsp;R, Levy&nbsp;Karin&nbsp;E, Mirdita&nbsp;M, Steinegger&nbsp;M.
                    <a href="https://academic.oup.com/nar/advance-article/doi/10.1093/nar/gkae1119/7906834" target="_blank" rel="noopener">BFVD - a large repository of predicted viral protein structures.</a>
                    Nucleic Acids Research,&nbsp;gkae1119,&nbsp;2024.
                </p>
            </v-card-title>
            </v-col>
        </v-card>
    </v-col>
</v-row>
</template>

<script>
import Panel from "./Panel.vue";
import StructureViewer from "./StructureViewer.vue";
import Members from "./Members.vue";
import TaxSpan from "./TaxSpan.vue";
import ExternalLinks from "./ExternalLinks.vue";
import Fragment from "./Fragment.vue";
import Similars from "./Similars.vue";
import MsaLogoPlot from "./logoplot/MsaLogoPlot.vue"
// import Annotations from "./Annotations.vue";

export default {
    name: "cluster",
    components: {
        Panel,
        StructureViewer,
        Members,
        TaxSpan,
        ExternalLinks,
        Fragment,
        Similars,
        MsaLogoPlot
        // Annotations,
    },
    data() {
        return {
            dialog: false,
            cluster: null,
            response: null,
            fetching: false,
            second: "",
        }
    },
    mounted() {
        this.fetchData();
    },
    watch: {
        $route(to, from) {
            if (to.params.cluster === from.params.cluster) {
                return;
            }

            this.fetchData();
        }
    },
    methods: {
        log(value) {
            console.log(value);
        },
        formatNumber(value) {
            if (value === null || value === undefined || value === '') {
                return 'NA';
            }
            const number = Number(value);
            return Number.isFinite(number) ? number.toFixed(2) : 'NA';
        },
        fetchData() {
            this.fetching = true;
            this.cluster = this.$route.params.cluster;
            if (!this.cluster) {
                return;
            }

            this.$axios.get("/cluster/" + this.$route.params.cluster)
                .then(response => {
                    this.response = response.data;
                })
                .catch((result) => {
                    if (!result || !result.response || result.response.status != 404) {
                        return;
                    }
                    this.$axios.get("/" + this.$route.params.cluster)
                        .then(response => {
                            this.$router.replace({ name: "cluster", params: { cluster: response.data[0].accession } });
                        })
                        .catch(() => {});
                })
                .finally(() => {
                    this.fetching = false;
                });
        }
    }
}

</script>

<style scoped>
dl {
  display: grid;
  padding-top: .25em;
  padding-bottom: 1em;
  grid-gap: 1em;
}

.dl-3 {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.dl-4 {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

dt {
    font-weight: bold;
}

.annot-label {
    font-size: 0.75em;
    opacity: 0.7;
    font-weight: normal;
    border: 1px solid currentColor;
    border-radius: 3px;
    padding: 0 0.35em;
    margin-left: 0.5em;
    white-space: nowrap;
}

a.annot-label {
    text-decoration: none;
    /* Box keeps the BFVD green; the text takes the surrounding default colour. The base
       rule draws the border in currentColor, so it has to be set explicitly here. */
    color: inherit;
    border-color: #9ED19F;
    opacity: 1;
}

@media screen and (min-width: 961px) {
    .repr-structure {
        margin-left: 0em;
    }
}

@media screen and (max-width: 960px) {
    .repr-structure {
        margin-top: 0em;
    }
}
</style>
